/**
 * Theme mechanism — one source of truth for the storage key, the reader-route
 * carve-out, and the pre-paint init script.
 *
 * The app has no theme library (deliberately — no new dependencies). The whole
 * mechanism is: a `.dark` class on `<html>`, a `localStorage` preference, and
 * `prefers-color-scheme` as the default. `globals.css` already defines the full
 * `.dark` token block, so applying the class is all that is required.
 *
 * Two route trees are exempt because they already own their appearance:
 *
 * - **The reader.** Story 4.4 stores a per-user light/sepia/dark colour pair and
 *   writes it as CSS variables inside the R2D2BC iframe, which never reads this
 *   class. An app theme painting the reader chrome would put two independent
 *   theme sources on one screen.
 * - **Admin.** `admin-design-system.md` (UX-DR55 / Story 8.1) fixes an explicit
 *   operations palette and states that stories must not change it. Its shell
 *   hard-codes those colours and so ignores `.dark` anyway — but the shadcn
 *   controls inside it do follow the tokens, so without this exemption dark mode
 *   would drop dark buttons and badges onto a light admin shell.
 */

export const THEME_STORAGE_KEY = 'bk-theme'

/**
 * Route prefixes that own their own theming and must never carry `.dark`.
 * Matched with `startsWith`, so `/admin` covers `/admin/users` as well.
 */
export const THEME_EXEMPT_PREFIXES = ['/read/', '/admin'] as const

export type Theme = 'light' | 'dark'

export function isThemeExemptRoute(pathname: string): boolean {
  return THEME_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/**
 * The stored preference, or `null` when unset or unreadable.
 *
 * `localStorage` throws rather than returning null in Safari private mode and
 * under a blocked-cookies policy, so every access is guarded — a browser that
 * refuses storage still gets a working (session-only) toggle.
 */
export function readStoredTheme(): Theme | null {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    return raw === 'light' || raw === 'dark' ? raw : null
  } catch {
    return null
  }
}

export function writeStoredTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Storage unavailable — the class is still applied, it just won't survive
    // a reload. Silently degrading beats blocking the toggle.
  }
}

export function prefersDark(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )
}

/** The theme that should be active right now for `pathname`. */
export function resolveTheme(pathname: string): Theme {
  if (isThemeExemptRoute(pathname)) return 'light'
  return readStoredTheme() ?? (prefersDark() ? 'dark' : 'light')
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark')
}

/**
 * Runs in `<head>`, before first paint, so a dark-preferring visitor never sees
 * a white flash. Inlined as a string because a module script would be deferred
 * past paint.
 *
 * It repeats the exempt-route check rather than delegating to
 * `ThemeRouteGuard`: the guard is a React effect and only runs after hydration,
 * so a *direct* load of `/read/x` would otherwise flash dark chrome around the
 * book. The duplication is one predicate, and the alternative is a visible flash.
 */
export const THEME_INIT_SCRIPT = `(function(){try{
var p=location.pathname,x=${JSON.stringify(THEME_EXEMPT_PREFIXES)};
for(var i=0;i<x.length;i++)if(p.indexOf(x[i])===0)return;
var s=null;try{s=localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)})}catch(e){}
var d=s==='dark'||(s!=='light'&&window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches);
if(d)document.documentElement.classList.add('dark');
}catch(e){}})()`
